import { memo, useCallback } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ConfigurationOptionDialogs from "./ConfigurationOptionDialogs";
import FluxmedOptionCard from "./FluxmedOptionCard";
import MetaOptionCard from "./MetaOptionCard";
import { useCrmSetupConfigurationOptionsPanel } from "../hooks/useCrmSetupConfigurationOptionsPanel";
import type { UseCrmSetupConfigurationResult } from "../hooks/useCrmSetupConfiguration";
import type { CrmSetupConfigurationOption } from "../types";
import { useSecretaryAgentContext } from "@/contexts/SecretaryAgentContext";
import { PatchContract } from "@/services/fhir/contract/PatchContract";

export type CrmSetupConfigurationOptionsPanelProps =
  UseCrmSetupConfigurationResult & {
    /** Quando false, bloqueia vinculação Meta até assinar a extensão FluxZap. */
    isFluxZapExtensionActive?: boolean;
    hasConfiguredAutomations?: boolean;
    removeAllAutomationConfigs?: () => Promise<void>;
    persistedWhatsappChannelOption?: CrmSetupConfigurationOption | null;
    onAfterChannelChange?: () => void | Promise<void>;
  };

const CrmSetupConfigurationOptionsPanel = memo(
  (props: CrmSetupConfigurationOptionsPanelProps) => {
    const {
      isLoadingConfiguration,
      isSavingOption,
      isSavingMetaConnection,
      isMetaConnected,
      disconnectMetaAccount,
      hasMetaEmbeddedSignupConfig,
      isSecureContextForMeta,
      isMetaEmbeddedSignupReady,
      isMetaEmbeddedSignupLoading,
      metaEmbeddedSignupError,
      isFluxZapExtensionActive = false,
      hasConfiguredAutomations = false,
      removeAllAutomationConfigs,
      persistedWhatsappChannelOption = null,
      onAfterChannelChange,
    } = props;

    const { contractSecretaryAgent, refetchContract: refetchSecretaryAgent } =
      useSecretaryAgentContext();
    const hasSecretaryAgentActive = Boolean(contractSecretaryAgent?.id);

    const cancelSecretaryAgentIfActive = useCallback(async () => {
      if (!contractSecretaryAgent?.id) return;
      await PatchContract({
        contractId: contractSecretaryAgent.id,
        operation: "replace",
        path: "/status",
        content: "cancelled",
      });
      await refetchSecretaryAgent();
    }, [contractSecretaryAgent?.id, refetchSecretaryAgent]);

    const {
      metaConnection,
      isConfirmDialogOpen,
      isFluxmedConfirmOpen,
      metaConnectionMode,
      manualWabaId,
      selectedManualPhoneContactId,
      manualPhoneContacts,
      isLoadingManualPhoneContacts,
      manualPhoneLookupError,
      manualValidationError,
      hasManualWabaLookupSucceeded,
      isManualWabaInputVisible,
      partnerIdCopied,
      isUsingDefaultOption,
      isUsingMetaOption,
      shouldDimMetaCard,
      shouldDimFluxmedCard,
      isMetaFullyConnected,
      isMetaCardHighlighted,
      showMetaDisconnectButton,
      showFluxmedCardHighlight,
      handleConnectMetaClick,
      handleConfirmConnectMeta,
      handleCancelConnectMeta,
      handleMetaConnectionModeChange,
      handleCopyPartnerId,
      handleManualWabaIdChange,
      handleShowManualWabaInput,
      handleSelectManualPhoneContact,
      handleManualLinking,
      handleSelectFluxmedClick,
      handleConfirmFluxmed,
      handleCancelFluxmed,
    } = useCrmSetupConfigurationOptionsPanel(props, {
      isFluxZapExtensionActive,
      hasConfiguredAutomations,
      removeAllAutomationConfigs,
      persistedWhatsappChannelOption,
      onAfterChannelChange,
      hasSecretaryAgentActive,
      cancelSecretaryAgentIfActive,
    });

    if (isLoadingConfiguration) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
          <CircularProgress size={24} />
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        <Typography variant="subtitle2" color="text.secondary">
          Selecione uma das opções:
        </Typography>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="stretch"
        >
          <MetaOptionCard
            isMetaFullyConnected={isMetaFullyConnected}
            isMetaCardHighlighted={isMetaCardHighlighted}
            shouldDimMetaCard={shouldDimMetaCard}
            isUsingMetaOption={isUsingMetaOption}
            isFluxZapExtensionActive={isFluxZapExtensionActive}
            metaConnectionMode={metaConnectionMode}
            hasManualWabaLookupSucceeded={hasManualWabaLookupSucceeded}
            partnerIdCopied={partnerIdCopied}
            isManualWabaInputVisible={isManualWabaInputVisible}
            manualValidationError={manualValidationError}
            manualWabaId={manualWabaId}
            isLoadingManualPhoneContacts={isLoadingManualPhoneContacts}
            manualPhoneLookupError={manualPhoneLookupError}
            manualPhoneContacts={manualPhoneContacts}
            selectedManualPhoneContactId={selectedManualPhoneContactId}
            hasMetaEmbeddedSignupConfig={hasMetaEmbeddedSignupConfig}
            isSecureContextForMeta={isSecureContextForMeta}
            metaEmbeddedSignupError={metaEmbeddedSignupError}
            isMetaConnected={isMetaConnected}
            metaConnectionWabaId={metaConnection?.wabaId}
            showMetaDisconnectButton={showMetaDisconnectButton}
            isSavingMetaConnection={isSavingMetaConnection}
            isSavingOption={isSavingOption}
            isMetaEmbeddedSignupReady={isMetaEmbeddedSignupReady}
            isMetaEmbeddedSignupLoading={isMetaEmbeddedSignupLoading}
            onMetaConnectionModeChange={handleMetaConnectionModeChange}
            onCopyPartnerId={handleCopyPartnerId}
            onManualWabaIdChange={handleManualWabaIdChange}
            onShowManualWabaInput={handleShowManualWabaInput}
            onSelectManualPhoneContact={handleSelectManualPhoneContact}
            onDisconnectMetaAccount={() => void disconnectMetaAccount()}
            onManualLinking={() => void handleManualLinking()}
            onConnectMetaClick={handleConnectMetaClick}
          />
          <FluxmedOptionCard
            showFluxmedCardHighlight={showFluxmedCardHighlight}
            shouldDimFluxmedCard={shouldDimFluxmedCard}
            isUsingDefaultOption={isUsingDefaultOption}
            isSavingOption={isSavingOption}
            onSelectFluxmedClick={handleSelectFluxmedClick}
          />
        </Stack>

        <ConfigurationOptionDialogs
          isConfirmDialogOpen={isConfirmDialogOpen}
          isFluxmedConfirmOpen={isFluxmedConfirmOpen}
          onCancelConnectMeta={handleCancelConnectMeta}
          onConfirmConnectMeta={() => void handleConfirmConnectMeta()}
          onCancelFluxmed={handleCancelFluxmed}
          onConfirmFluxmed={() => void handleConfirmFluxmed()}
          hasConfiguredAutomations={hasConfiguredAutomations}
          hasSecretaryAgentActive={hasSecretaryAgentActive}
        />
      </Stack>
    );
  },
);

CrmSetupConfigurationOptionsPanel.displayName =
  "CrmSetupConfigurationOptionsPanel";

export default CrmSetupConfigurationOptionsPanel;
